import React from 'react';
import { Modal, Checkbox, Button, type CheckboxChangeEvent } from '@app/ui';
import { ExclamationCircleOutlined, CloseOutlined } from '@ant-design/icons';
import { useTranslation, Trans } from 'react-i18next';
import { AttributionMode } from './types';
import './ToggleConfirmationModal.scss';

interface ModalContent {
    title: string;
    description: string;
    note: string;
    confirmText: string;
    cancelText: string;
    descriptionSmpPage?: string;
}

interface ToggleConfirmationModalProps {
    visible: boolean;
    currentMode: AttributionMode;
    // The mode confirming will switch to. Defaults to the inverse of
    // currentMode, which is only the same thing while the mode does not change
    // underneath the open modal.
    targetMode?: AttributionMode;
    onConfirm: () => void;
    onCancel: () => void;
    onDontShowAgain: (checked: boolean) => void;
    dontShowAgain?: boolean;
    isOnReportsPage: boolean;
    isOnAutomatedInsightsPage?: boolean;
    isOnSmpPage?: boolean;
}

const ToggleConfirmationModal: React.FC<ToggleConfirmationModalProps> = ({
    visible,
    currentMode,
    targetMode: targetModeProp,
    onConfirm,
    onCancel,
    onDontShowAgain,
    dontShowAgain = false,
    isOnReportsPage,
    isOnAutomatedInsightsPage = false,
    isOnSmpPage = false,
}) => {
    const { t } = useTranslation();

    const targetMode: AttributionMode =
        targetModeProp ?? (currentMode === 'team' ? 'league' : 'team');

    const handleDontShowAgainChange = (e: CheckboxChangeEvent) => {
        onDontShowAgain(e.target.checked);
    };

    const getModalContent = (mode: AttributionMode = 'team'): ModalContent => {
        switch (mode) {
            case 'league':
                return {
                    title: t(
                        'rightsholderAttribution.modal.switchingToLeague.title',
                    ),
                    description: t(
                        'rightsholderAttribution.modal.switchingToLeague.description',
                    ),
                    note: t(
                        'rightsholderAttribution.modal.switchingToLeague.note',
                    ),
                    confirmText: t(
                        'rightsholderAttribution.modal.switchingToLeague.confirmText',
                    ),
                    cancelText: t(
                        'rightsholderAttribution.modal.switchingToLeague.cancelText',
                    ),
                    descriptionSmpPage: t(
                        'rightsholderAttribution.modal.switchingToLeague.descriptionSmpPage',
                    ),
                };
            case 'team':
            default:
                return {
                    title: t(
                        'rightsholderAttribution.modal.switchingToTeam.title',
                    ),
                    description: t(
                        'rightsholderAttribution.modal.switchingToTeam.description',
                    ),
                    note: t(
                        'rightsholderAttribution.modal.switchingToTeam.note',
                    ),
                    confirmText: t(
                        'rightsholderAttribution.modal.switchingToTeam.confirmText',
                    ),
                    cancelText: t(
                        'rightsholderAttribution.modal.switchingToTeam.cancelText',
                    ),
                    descriptionSmpPage: t(
                        'rightsholderAttribution.modal.switchingToTeam.descriptionSmpPage',
                    ),
                };
        }
    };

    const modalContent = getModalContent(targetMode);

    const descriptionI18nKey = `rightsholderAttribution.modal.${
        targetMode === 'league' ? 'switchingToLeague' : 'switchingToTeam'
    }.${isOnSmpPage ? 'descriptionSmpPage' : 'description'}`;

    const noteText = isOnReportsPage
        ? t('rightsholderAttribution.modal.reportsPageNote')
        : isOnAutomatedInsightsPage
          ? t('rightsholderAttribution.modal.automatedInsightsNote')
          : modalContent.note;

    return (
        <Modal
            title={
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    <ExclamationCircleOutlined />
                    <span
                        style={{
                            fontSize: '16px',
                            fontWeight: '500',
                        }}
                    >
                        {modalContent.title}
                    </span>
                </div>
            }
            open={visible}
            onCancel={onCancel}
            footer={null}
            width={600}
            styles={{
                header: {
                    borderRadius: '8px 8px 0 0',
                    textAlign: 'center',
                },
                body: {},
                content: {
                    borderRadius: '8px',
                },
            }}
            closeIcon={<CloseOutlined />}
        >
            <div className="toggle-confirmation-modal__content">
                <p style={{ margin: '0 0 16px 0', lineHeight: '1.6' }}>
                    <Trans
                        i18nKey={descriptionI18nKey}
                        components={{
                            censusLink: (
                                <a
                                    className="toggle-confirmation-modal__info-link"
                                    href="https://relometrics.freshdesk.com/support/solutions/articles/156000369235"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                />
                            ),
                        }}
                    />
                </p>

                <p style={{ margin: '0 0 16px 0', lineHeight: '1.6' }}>
                    ⚠️{' '}
                    <span style={{ fontWeight: '600' }}>
                        {t('rightsholderAttribution.modal.noteLabel')}
                    </span>{' '}
                    {noteText}
                </p>

                <p style={{ margin: '0 0 16px 0' }}>
                    <a
                        className="toggle-confirmation-modal__info-link"
                        href="https://relometrics.freshdesk.com/support/solutions/articles/156000388343-rights-holder-attribution-in-edge-overview"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {t('rightsholderAttribution.modal.learnMoreLink')}
                    </a>
                </p>

                <div className="toggle-confirmation-modal__checkbox-container">
                    <Checkbox
                        checked={dontShowAgain}
                        onChange={handleDontShowAgainChange}
                    >
                        {t('rightsholderAttribution.modal.dontShowAgain')}
                    </Checkbox>
                </div>

                <div className="toggle-confirmation-modal__button-container">
                    <Button
                        color="primary"
                        variant="outlined"
                        onClick={onCancel}
                    >
                        {modalContent.cancelText}
                    </Button>
                    <Button
                        type="primary"
                        color="primary"
                        variant="solid"
                        onClick={onConfirm}
                    >
                        {modalContent.confirmText}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default ToggleConfirmationModal;
